import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isArchivePending,
  maxLookupDateIso,
  minLookupDateIso,
  validateFlightQuery,
} from "./handler/validation.helper";
import {
  ArrivingIcon,
  DepartingIcon,
  InfoIcon,
  SpinnerIcon,
  TrajectoryPlateArrivingIcon,
  TrajectoryPlateIcon,
} from "./icons";
import { FlightDirection, FlightLookupError } from "./types";

export interface FlightFormStepProps {
  direction: FlightDirection;
  error: FlightLookupError | null;

  isLoading: boolean;
  onSubmit: (flightNumber: string, isoDate: string) => void;
  onChangeDirection: () => void;
}

export const FlightFormStep: React.FC<FlightFormStepProps> = ({
  direction,
  error,
  isLoading,
  onSubmit,
  onChangeDirection,
}) => {
  const { t } = useTranslation("flightAlerts");
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [flightNumber, setFlightNumber] = useState("");
  const [isoDate, setIsoDate] = useState("");
  const [localError, setLocalError] = useState<
    FlightLookupError["type"] | null
  >(null);
  const [lastSubmitted, setLastSubmitted] = useState<{
    flightNumber: string;
    isoDate: string;
  } | null>(null);

  const isArrival = direction === "arrival";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const validation = validateFlightQuery(flightNumber, isoDate);
    if (!validation.valid) {
      setLocalError(validation.error ?? "malformed");
      return;
    }
    setLocalError(null);
    setLastSubmitted({ flightNumber, isoDate });
    onSubmit(flightNumber, isoDate);
  };

  const displayedError = localError ?? error?.type ?? null;

  const HeroIcon = isArrival ? ArrivingIcon : DepartingIcon;

  const dateDisplay = isoDate
    ? new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <>
      <h1 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100">
        {isArrival ? t("flight.headingArrival") : t("flight.headingDeparture")}
      </h1>

      <div
        className={`relative w-full h-[88px] rounded-xl overflow-hidden ${
          isArrival ? "bg-app" : "bg-primary-tint"
        }`}
      >
        {isArrival ? (
          <TrajectoryPlateArrivingIcon className="absolute left-0 top-0 h-full w-[345px] text-ink-100" />
        ) : (
          <TrajectoryPlateIcon className="absolute left-0 top-0 h-full w-[345px] text-brand-blue" />
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:gap-5 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8"
      >
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full py-[5px] px-2.5 ${
              isArrival ? "bg-ink-100" : "bg-brand-blue"
            }`}
          >
            <HeroIcon className="h-3.5 w-auto text-white" />

            <span className="font-lexend text-[10px]/[12px] font-bold tracking-[0.6px] uppercase text-white">
              {isArrival
                ? t("flight.badgeArriving")
                : t("flight.badgeDeparting")}
            </span>
          </span>
          <button
            type="button"
            className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-brand-blue"
            onClick={onChangeDirection}
          >
            {t("flight.change")}
          </button>
        </div>

        <div className="h-px w-full bg-ink-10" />

        <div className="flex flex-col gap-2">
          <label
            htmlFor="flight-number"
            className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60"
          >
            {t("flight.numberLabel")}
          </label>

          <input
            id="flight-number"
            aria-label={t("flight.numberLabel")}
            value={flightNumber}
            onChange={(e) => setFlightNumber(e.target.value)}
            placeholder={t("flight.numberPlaceholder")}
            className={`h-14 rounded-2xl border-2 bg-app px-4 font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-100 placeholder:text-ink-40 ${
              displayedError === "malformed" ||
              displayedError === "wrongDirection"
                ? "border-danger-100"
                : "border-ink-12"
            }`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="flight-date"
            className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60"
          >
            {isArrival
              ? t("flight.dateLabelArrival")
              : t("flight.dateLabelDeparture")}
          </label>

          <div className="relative h-14">
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute inset-0 flex items-center rounded-2xl border-2 bg-app px-4 ${
                displayedError === "outOfRange"
                  ? "border-danger-100"
                  : "border-ink-12"
              }`}
            >
              <span
                className={`font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] ${
                  dateDisplay ? "text-ink-100" : "text-ink-40"
                }`}
              >
                {dateDisplay ?? t("flight.datePlaceholder")}
              </span>
            </div>
            <input
              id="flight-date"
              ref={dateInputRef}
              type="date"
              aria-label={
                isArrival
                  ? t("flight.dateLabelArrival")
                  : t("flight.dateLabelDeparture")
              }
              value={isoDate}
              min={minLookupDateIso()}
              max={maxLookupDateIso()}
              onChange={(e) => setIsoDate(e.target.value)}
              onClick={() => dateInputRef.current?.showPicker?.()}
              className="absolute inset-0 h-14 w-full cursor-pointer opacity-0"
            />
          </div>
        </div>

        {displayedError === "malformed" && (
          <div className="flex items-start gap-2 rounded-xl bg-danger-tint p-3">
            <InfoIcon className="h-4 w-4 flex-shrink-0 text-danger-100" />
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
              {t("flight.errInvalidFormat")}
            </p>
          </div>
        )}
        {displayedError === "outOfRange" && (
          <div className="flex items-start gap-2 rounded-xl bg-danger-tint p-3">
            <InfoIcon className="h-4 w-4 flex-shrink-0 text-danger-100" />
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
              {t("flight.errOutOfRange")}
            </p>
          </div>
        )}

        {(displayedError === "notFound" ||
          displayedError === "wrongDirection") && (
          <div className="flex items-start gap-2 rounded-xl bg-danger-tint p-3">
            <InfoIcon className="h-4 w-4 flex-shrink-0 text-danger-100" />
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
              {displayedError === "notFound" &&
              isArchivePending(lastSubmitted?.isoDate || isoDate)
                ? t("flight.errYesterdayNotArchived")
                : t("flight.errNotFound", {
                    direction: isArrival
                      ? t("flight.directionWordArriving")
                      : t("flight.directionWordDeparting"),
                    flightNumber:
                      lastSubmitted?.flightNumber || flightNumber || "—",
                    date: lastSubmitted?.isoDate || isoDate || "—",
                  })}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          className={`flex h-14 items-center justify-center gap-2 rounded-2xl bg-brand-blue ${
            isLoading ? "cursor-not-allowed opacity-70" : ""
          }`}
        >
          {isLoading && (
            <SpinnerIcon className="h-4 w-4 animate-spin text-white" />
          )}
          <span className="font-lexend text-[16px]/[20px] font-bold tracking-normal text-white">
            {isLoading ? t("flight.submitting") : t("flight.submit")}
          </span>
        </button>
      </form>
    </>
  );
};
